<template>
    <div class="flex flex-col items-center gap-4">
        <div class="w-full flex flex-col md:flex-row items-center gap-6">
            <div class="relative w-60 h-60 md:w-48 md:h-48 rounded-full overflow-hidden border-2 border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 flex-shrink-0 flex items-center justify-center shadow-inner avatar-cropper">
                <img v-if="profile.avatarUrl && !image" :key="profile.avatarUrl" :src="profile.avatarUrl" class="w-full h-full object-cover" />
                <UserIcon v-else-if="!image" class="w-24 h-24 text-gray-400" />
                
                <Cropper
                    v-if="image"
                    ref="cropperRef"
                    class="w-full h-full absolute inset-0 touch-none"
                    :src="image"
                    :stencil-props="{
                        aspectRatio: 1,
                        movable: true,
                        scalable: true,
                    }"
                    :stencil-component="CircleStencil"
                    image-restriction="stencil"
                />
            </div>
            
            <div class="flex flex-col gap-3">
                <input type="file" ref="fileInput" accept="image/*" class="hidden" @change="onFileChange" />
                
                <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ $t('profileSection.uploadAvatarHelp') }}
                </p>

                <div class="flex flex-row gap-3">
                    <button @click="pickImage" class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white border-none cursor-pointer rounded-lg hover:opacity-90 transition-opacity">
                        {{ image ? $t('profileSection.changeImage') : $t('profileSection.uploadImage') }}
                    </button>
                    <button v-if="image" @click="saveAvatar" class="px-4 py-2 bg-blue-500 text-white border-none cursor-pointer rounded-lg hover:opacity-90 transition-opacity" :disabled="saving">
                        <span v-if="saving">{{ $t('profileSection.saving') }}</span>
                        <span v-else>{{ $t('profileSection.saveAvatar') }}</span>
                    </button>
                    <button v-if="image" @click="cancelEdit" class="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-none cursor-pointer rounded-lg hover:opacity-90 transition-opacity" :disabled="saving">
                        {{ $t('form.cancel') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { Cropper, CircleStencil } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { UserIcon } from '@heroicons/vue/24/outline'
import toastStore from '@/store/toast.store'
import userStore from '@/store/user.store'
import { isNativePlatform } from '@/utils/capacitor'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'

const { t } = useI18n();

defineProps({
    profile: {
        type: Object,
        required: true
    }
})

const emit = defineEmits(['avatar-updated'])

const fileInput = ref(null)
const cropperRef = ref(null)
const image = ref(null)
const saving = ref(false)
const selectedFile = ref(null)

const onFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
        setSelectedImage(file)
    }
}

const dataUrlToFile = (dataUrl, filename) => {
    // Conversão síncrona (sem fetch/FileReader: data URLs grandes penduram
    // o fetch no WebView em alguns aparelhos).
    const parts = String(dataUrl).split(',');
    const mimeMatch = /data:(.*?);base64/.exec(parts[0] || '');
    const mime = (mimeMatch && mimeMatch[1]) || 'image/jpeg';
    const binary = atob(parts[1] || '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
}

const setSelectedImage = (file) => {
    selectedFile.value = file
    if (image.value) {
        URL.revokeObjectURL(image.value)
    }
    image.value = URL.createObjectURL(file)
}

// No nativo usa o plugin Camera (não mata a activity como o file picker
// do sistema, que faz o WebView reiniciar e perder a foto escolhida).
const pickImage = async () => {
    if (!isNativePlatform()) {
        fileInput.value?.click()
        return
    }
    try {
        console.log('[avatar]', 'abrindo galeria nativa...')
        const photo = await Camera.getPhoto({
            quality: 90,
            resultType: CameraResultType.DataUrl,
            source: CameraSource.Photos
        });
        console.log('[avatar]', 'foto recebida: ' + (photo && photo.dataUrl ? `${photo.format || '?'}, ${Math.round((photo.dataUrl.length || 0) / 1024)}KB` : 'VAZIA'))
        if (photo && photo.dataUrl) {
            let file = null;
            try {
                file = dataUrlToFile(photo.dataUrl, 'avatar.jpg')
            } catch (convErr) {
                console.log('[avatar]', 'falha ao converter foto: ' + (convErr && convErr.message))
            }
            if (!file) return;
            console.log('[avatar]', `arquivo pronto: ${file.name}, ${Math.round(file.size / 1024)}KB`)
            try {
                setSelectedImage(file)
                console.log('[avatar]', 'cropper aberto — ajuste e salve')
            } catch (cropErr) {
                console.log('[avatar]', 'falha ao abrir editor: ' + (cropErr && cropErr.message))
            }
        } else {
            console.log('[avatar]', 'plugin retornou sem imagem — tente o seletor de arquivos')
            try {
                fileInput.value?.click()
            } catch (err) {
                // sem fallback disponível
            }
        }
    } catch (e) {
        // Cancelado pelo usuário ou plugin indisponível: fallback ao input.
        if (e && e.message && !/cancel|cancelled|dismiss/i.test(e.message)) {
            console.warn('Camera plugin fallback to file input:', e);
        }
        try {
            fileInput.value?.click()
        } catch (err) {
            // sem fallback disponível
        }
    }
}

const cancelEdit = () => {
    if (image.value) {
        URL.revokeObjectURL(image.value)
    }
    image.value = null
    selectedFile.value = null
    if (fileInput.value) fileInput.value.value = ''
}

const saveAvatar = async () => {
    if (!cropperRef.value || !selectedFile.value) return
    saving.value = true
    
    const { canvas, coordinates } = cropperRef.value.getResult()
    
    if (canvas) {
        canvas.toBlob(async (blob) => {
            const formData = new FormData()
            formData.append('avatar', blob, 'avatar.png')
            formData.append('avatar_config', JSON.stringify(coordinates))
            
            try {
                const response = await fetch('/api/user/avatar', {
                    method: 'POST',
                    body: formData
                })
                
                if (response.ok) {
                    const result = await response.json()
                    // Cache-buster: o objeto pode levar segundos para propagar
                    // no R2/CDN e o 404 inicial ficava cacheado — sem isso a
                    // prévia só aparecia após sair e voltar à tela.
                    const freshUrl = result.avatarUrl
                        ? result.avatarUrl + (result.avatarUrl.includes('?') ? '&' : '?') + 'v=' + Date.now()
                        : result.avatarUrl
                    emit('avatar-updated', freshUrl)
                    cancelEdit()
                    // force=true: sem isso o store retorna o cache em memória
                    // e o avatar do SideMenu não atualiza.
                    userStore.dispatch('info', true)
                    toastStore.dispatch('addMessage', { message: t('profileSection.avatarUpdated'), icon: 'success' })
                } else {
                    const err = await response.json()
                    throw new Error(err.message || 'Upload failed')
                }
            } catch (err) {
                console.error(err)
                toastStore.dispatch('addMessage', { message: t('profileSection.avatarFailed'), icon: 'error' })
            } finally {
                saving.value = false
            }
        }, 'image/png')
    }
}
</script>

<style scoped>
/* Alvos de toque maiores no cropper (mobile): handlers padrão são pequenos
   demais para o dedo; sem isso é quase impossível arrastar as bordas. */
.avatar-cropper :deep(.vue-handler-wrapper) {
    width: 44px !important;
    height: 44px !important;
}
.avatar-cropper :deep(.vue-handler-wrapper .vue-handler) {
    transform: scale(1.4);
}
.avatar-cropper :deep(.vue-advanced-cropper__background),
.avatar-cropper :deep(.vue-advanced-cropper__foreground) {
    touch-action: none;
}
</style>
